import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { supabase } from "./supabase";
import { getDeviceId } from "./utils/device";
import type { UserProfile } from "./types";
import type { Session } from "@supabase/supabase-js";

export interface AllergyProfileData {
  allergens: string[];
  custom_allergens: string[];
}

export interface WeightGoalData {
  start_weight_kg: number;
  target_weight_kg: number;
  target_date: string;
  weekly_target_kg: number;
}

interface UserDataContextValue {
  profile: UserProfile | null;
  setProfile: (p: UserProfile | null) => void;
  saveProfile: (p: Partial<UserProfile>) => Promise<void>;
  allergyProfile: AllergyProfileData | null;
  saveAllergyProfile: (a: AllergyProfileData) => Promise<void>;
  weightGoal: WeightGoalData | null;
  saveWeightGoal: (g: WeightGoalData) => Promise<void>;
  loading: boolean;
}

const UserDataContext = createContext<UserDataContextValue | null>(null);

export function UserDataProvider({ children }: { session: Session | null; children: ReactNode }) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [allergyProfile, setAllergyProfile] = useState<AllergyProfileData | null>(null);
  const [weightGoal, setWeightGoal] = useState<WeightGoalData | null>(null);
  const [loading, setLoading] = useState(true);

  const deviceId = getDeviceId();

  const loadAll = useCallback(async () => {
    setLoading(true);

    const { data: profileData } = await supabase
      .from("user_profiles")
      .select("*")
      .eq("device_id", deviceId)
      .maybeSingle();
    if (profileData) setProfile(profileData as UserProfile);

    const { data: allergyData } = await supabase
      .from("allergy_profiles")
      .select("allergens,custom_allergens")
      .eq("device_id", deviceId)
      .maybeSingle();
    if (allergyData) setAllergyProfile(allergyData as AllergyProfileData);

    const { data: goalData } = await supabase
      .from("weight_loss_goals")
      .select("start_weight_kg,target_weight_kg,target_date,weekly_target_kg")
      .eq("device_id", deviceId)
      .maybeSingle();
    if (goalData) setWeightGoal(goalData as WeightGoalData);

    setLoading(false);
  }, [deviceId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveProfile = useCallback(async (p: Partial<UserProfile>) => {
    const merged = { ...profile, ...p, device_id: deviceId } as UserProfile;
    const { error } = await supabase
      .from("user_profiles")
      .upsert(merged, { onConflict: "device_id" });
    if (error) throw error;
    setProfile(merged);
  }, [profile, deviceId]);

  const saveAllergyProfile = useCallback(async (a: AllergyProfileData) => {
    const { error } = await supabase
      .from("allergy_profiles")
      .upsert({ device_id: deviceId, ...a }, { onConflict: "device_id" });
    if (error) throw error;
    setAllergyProfile(a);
  }, [deviceId]);

  const saveWeightGoal = useCallback(async (g: WeightGoalData) => {
    const { error } = await supabase
      .from("weight_loss_goals")
      .upsert({ device_id: deviceId, ...g }, { onConflict: "device_id" });
    if (error) throw error;
    setWeightGoal(g);
  }, [deviceId]);

  return (
    <UserDataContext.Provider value={{
      profile, setProfile, saveProfile,
      allergyProfile, saveAllergyProfile,
      weightGoal, saveWeightGoal,
      loading,
    }}>
      {children}
    </UserDataContext.Provider>
  );
}

export function useUserData() {
  const ctx = useContext(UserDataContext);
  if (!ctx) throw new Error("useUserData must be used within UserDataProvider");
  return ctx;
}
